import L from "leaflet";

// === S3 BUCKET CONFIGURATION (for drawing links) ===
const S3_BASE = "https://ticketviewgis.s3.ca-central-1.amazonaws.com";
const ZIP_SEGMENT = "ZIP 4";
const EXT_ORDER = [".TIF", ".PDF"]; // prefer TIF; can be changed to prefer PDF

// === Layer Styling Configuration ===
// Define consistent colors for different infrastructure types
export const LAYER_STYLES = {
  // General Infrastructure - neutral colors
  general: { color: "#64748b", opacity: 0.7 }, // slate gray

  // Storm Water - cool gray/blue tones
  storm: { color: "#6b7280", opacity: 0.75 }, // gray for mains/lines
  stormPoint: { color: "#64748b", opacity: 0.8 }, // slightly lighter for points

  // Water Network - blue tones
  water: { color: "#2563eb", opacity: 0.8 }, // bright blue for mains
  waterPoint: { color: "#3b82f6", opacity: 0.85 }, // lighter blue for points

  // Wastewater - brown/amber tones
  wastewater: { color: "#92400e", opacity: 0.75 }, // dark brown for mains
  wastewaterPoint: { color: "#b45309", opacity: 0.8 }, // lighter brown for points
};

// === Layer definitions (without workspace prefix) ===
// These layer names are common across regions, with workspace prefix added dynamically
const LAYER_DEFINITIONS = [
  // General Infrastructure
  { layerName: "Roads", title: "Roads", style: "general" },
  { layerName: "Addresses", title: "Addresses", style: "general" },
  { layerName: "Boundary_RMW", title: "RMW Boundary", style: "general" },
  {
    layerName: "CityTownVillage",
    title: "City/Town/Village",
    style: "general",
  },

  // Storm Water System (RSW) - gray tones
  { layerName: "RSW_Mains", title: "Storm Mains", style: "storm" },
  {
    layerName: "RSW_Manholes",
    title: "Storm Manholes",
    style: "stormPoint",
  },
  {
    layerName: "RSW_Catchbasins",
    title: "Storm Catchbasins",
    style: "stormPoint",
  },
  { layerName: "RSW_Ceptors", title: "Storm Ceptors", style: "stormPoint" },
  { layerName: "RSW_Culverts", title: "Storm Culverts", style: "storm" },
  { layerName: "RSW_Ditches", title: "Storm Ditches", style: "storm" },
  { layerName: "RSW_Inlets", title: "Storm Inlets", style: "stormPoint" },
  { layerName: "RSW_Leads", title: "Storm Leads", style: "storm" },
  { layerName: "RSW_Outlets", title: "Storm Outlets", style: "stormPoint" },
  { layerName: "RSW_Ponds", title: "Storm Ponds", style: "stormPoint" },
  { layerName: "RSW_Subdrains", title: "Storm Subdrains", style: "storm" },

  // Water Network (RWN) - blue tones
  { layerName: "Water_Mains", title: "Water Mains", style: "water" },
  { layerName: "RWN_Mains", title: "Water Network Mains", style: "water" },
  { layerName: "RWN_Hydrants", title: "Fire Hydrants", style: "waterPoint" },
  {
    layerName: "RWN_Chambers",
    title: "Water Chambers",
    style: "waterPoint",
  },
  {
    layerName: "RWN_Junctions",
    title: "Water Junctions",
    style: "waterPoint",
  },
  {
    layerName: "RWN_ServiceValves",
    title: "Water Service Valves",
    style: "waterPoint",
  },
  { layerName: "RWN_Services", title: "Water Services", style: "water" },
  { layerName: "RWN_Valves", title: "Water Valves", style: "waterPoint" },
  {
    layerName: "Water_Services",
    title: "Water Services (Alt)",
    style: "water",
  },
  {
    layerName: "Water_Chambers",
    title: "Water Chambers (Alt)",
    style: "waterPoint",
  },
  {
    layerName: "Water_Valves",
    title: "Water Valves (Alt)",
    style: "waterPoint",
  },

  // Wastewater Network (RWWN) - brown tones
  {
    layerName: "RWWN_Mains",
    title: "Wastewater Mains",
    style: "wastewater",
  },
  {
    layerName: "RWWN_LateralLines",
    title: "Wastewater Lateral Lines",
    style: "wastewater",
  },
  {
    layerName: "RWWN_Manholes",
    title: "Wastewater Manholes",
    style: "wastewaterPoint",
  },
];

// === Region-specific layer definitions ===
// Halton Hills has different layer names than Waterloo
const HALTON_HILLS_LAYER_DEFINITIONS = [
  // General Infrastructure
  { layerName: "Civic_Address", title: "Civic Address", style: "general" },
  {
    layerName: "Parcel_Assessment",
    title: "Parcel Assessment",
    style: "general",
  },
  { layerName: "Parcel_Owner", title: "Parcel Owner", style: "general" },

  // Storm Water System (SS = Storm Sewer)
  {
    layerName: "SS_CATCHBASIN",
    title: "Storm Catchbasin",
    style: "stormPoint",
  },
  {
    layerName: "SS_INFILTRATION_G",
    title: "Storm Infiltration",
    style: "storm",
  },
  { layerName: "SS_MANHOLE", title: "Storm Manhole", style: "stormPoint" },
  { layerName: "SS_ML_LEAD", title: "Storm Main/Lead", style: "storm" },
  { layerName: "SS_OUTFALL", title: "Storm Outfall", style: "stormPoint" },
];

// === WMS CONFIGURATION ===
const WMS_BASE_URL_TEMPLATE =
  "https://gis.dev.ticketview.ca/geoserver/{workspace}/ows";
const WMS_AUTH_KEY = "e812bd39-649e-4b70-b008-b1e0f4360919";

// Region to GeoServer workspace mapping
// Maps region names (as stored in tickets) to GeoServer workspace names
export const REGION_WORKSPACE_MAP = {
  Waterloo: "waterloo",
  waterloo: "waterloo",
  Halton: "haltonhills",
  HALTON: "haltonhills",
  "Halton Hills": "haltonhills",
  "halton hills": "haltonhills",
  haltonhills: "haltonhills",
  // Add more regions as needed
};

// Default workspace (for backward compatibility)
const DEFAULT_WORKSPACE = "waterloo";

// Get workspace name from region
export function getWorkspaceFromRegion(region) {
  if (!region) return DEFAULT_WORKSPACE;

  const normalizedRegion = (region || "").toString().trim();

  // Direct lookup
  if (REGION_WORKSPACE_MAP[normalizedRegion]) {
    return REGION_WORKSPACE_MAP[normalizedRegion];
  }

  // Case-insensitive lookup
  const lowerRegion = normalizedRegion.toLowerCase();
  for (const [key, value] of Object.entries(REGION_WORKSPACE_MAP)) {
    if (key.toLowerCase() === lowerRegion) {
      return value;
    }
  }

  // Fallback to default
  return DEFAULT_WORKSPACE;
}

// Build WMS base URL for a specific workspace
export function buildWMSBaseUrl(workspace) {
  return WMS_BASE_URL_TEMPLATE.replace("{workspace}", workspace);
}

// Generate layers with workspace prefix for a specific region
export function getLayersForRegion(region = null) {
  const workspace = getWorkspaceFromRegion(region);

  // Use region-specific layer definitions if available
  let layerDefinitions = LAYER_DEFINITIONS; // Default to Waterloo layers
  if (workspace === "haltonhills") {
    layerDefinitions = HALTON_HILLS_LAYER_DEFINITIONS;
  }

  return layerDefinitions.map((def) => ({
    key: `${workspace}:${def.layerName}`,
    title: def.title,
    style: def.style,
  }));
}

// Default layers (for backward compatibility - uses Waterloo)
export const LAYERS = getLayersForRegion("waterloo");

// Function to search for addresses using Nominatim (OpenStreetMap geocoding)
export async function searchAddresses(searchTerm) {
  try {
    if (!searchTerm.trim()) return [];

    // Use Nominatim for geocoding (free, no API key required)
    const params = new URLSearchParams({
      q: searchTerm,
      format: "json",
      addressdetails: "1",
      limit: "10",
      // Bounding box for Waterloo region (expanded for better coverage)
      // Format: left,top,right,bottom (min_lon, max_lat, max_lon, min_lat)
      // Covers Waterloo, Kitchener, Cambridge, and surrounding areas
      viewbox: "-80.8,43.3,-80.2,43.7",
      bounded: "1", // Restrict results to viewbox
      countrycodes: "ca", // Restrict to Canada
    });

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      {
        headers: {
          "User-Agent": "WMS-Viewer/1.0", // Nominatim requires a User-Agent
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();

    return data.map((result) => ({
      id: result.place_id.toString(),
      address: result.display_name,
      coordinates: [parseFloat(result.lat), parseFloat(result.lon)],
      properties: {
        type: result.type,
        class: result.class,
        importance: result.importance,
        ...result.address,
      },
    }));
  } catch (error) {
    return [];
  }
}

// === DRAWING LINK HELPER FUNCTIONS (based on content.ftl logic) ===

// Encode a single path segment (not slashes)
export function encodeSegment(s) {
  if (!s) return "";
  return s
    .trim()
    .replace(/%/g, "%25")
    .replace(/&/g, "%26")
    .replace(/\+/g, "%2B")
    .replace(/#/g, "%23")
    .replace(/\s/g, "%20");
}

// Convert path to query param value where / -> %2F
export function pathToQueryParam(path) {
  if (!path) return "";
  return path.replace(/\//g, "%2F");
}

// Normalize ID: uppercase, remove extension, replace spaces/underscores with hyphen
export function normalizeId(raw) {
  if (!raw) return "";
  const upper = raw.toUpperCase();
  const noext = upper.replace(/\..*$/, "");
  return noext.replace(/[ _]+/g, "-");
}

// Check if ID has hyphen
export function hasHyphen(s) {
  return s.includes("-");
}

// Determine folder name from normalized ID
export function folderFromId(normId) {
  if (!normId) return "";
  if (hasHyphen(normId)) {
    const before = normId.split("-")[0];
    return before || (normId.length >= 6 ? normId.substring(0, 6) : "");
  }
  return normId;
}

// Derive ID from DataSource (e.g., BR2405_03 -> BR2405-003)
export function deriveIdFromDataSource(dataSource) {
  if (!dataSource) return "";
  const ds = dataSource.toUpperCase().trim();

  // Base + suffix (1-3 digits)
  const withSuffixMatch = ds.match(/([A-Z]{1,3}\d{3,5})[-_ ]?(\d{1,3})/);
  if (withSuffixMatch) {
    const base = withSuffixMatch[1];
    const suffix = withSuffixMatch[2];
    const suffix3 = suffix.padStart(3, "0");
    return `${base}-${suffix3}`;
  }

  // Base only
  const baseMatch = ds.match(/[A-Z]{1,3}\d{3,5}/);
  if (baseMatch) {
    return baseMatch[0];
  }

  return "";
}

// Zero-pad suffix to 3 digits (used only when derived from DataSource)
export function padSuffix3(id) {
  const match = id.match(/^([A-Z]{1,3}\d{3,5})-(\d{1,3})$/);
  if (match) {
    const base = match[1];
    const suffix = match[2].padStart(3, "0");
    return `${base}-${suffix}`;
  }
  return id;
}

// Determine utility folder from RegionalDivision field
export function canonicalUtilFromRegionalDivision(rd) {
  const upper = (rd || "").toUpperCase();
  if (
    upper.startsWith("WASTE") ||
    upper.startsWith("SANIT") ||
    upper.startsWith("SEWER")
  ) {
    return "WasteWater";
  }
  if (upper.startsWith("STORM") || upper.startsWith("DRAIN")) {
    return "Storm";
  }
  if (upper.startsWith("WATER")) {
    return "Water";
  }
  return "";
}

// Determine utility folder from layer name
export function canonicalUtilFromLayer(layerName) {
  const ln = (layerName || "").toUpperCase();
  if (ln.includes("RWWN")) return "WasteWater";
  if (ln.includes("RSW")) return "Storm";
  if (ln.includes("RWN") || ln.includes("WATER")) return "Water";
  return "Water"; // default
}

// Choose utility folder (prefer feature attribute, fallback to layer name)
export function chooseUtilityFolder(layerName, properties) {
  const fromFeature = canonicalUtilFromRegionalDivision(
    properties.RegionalDivision || ""
  );
  if (fromFeature) return fromFeature;
  return canonicalUtilFromLayer(layerName);
}

// Get area from Settlement or Municipality
export function getArea(properties) {
  const settlement = properties.Settlement || "";
  const municipality = properties.Municipality || "";
  return settlement || municipality || "Region-Wide";
}

// Generate viewer URL for a feature
export function generateViewerUrl(layerName, properties) {
  // Check if this is a custom AsBuilt file (has direct S3 URL)
  if (properties._isCustomAsBuilt && properties._s3Url) {
    // For custom AsBuilt files, use the S3 URL directly
    // If it's already a full URL, return it; otherwise construct viewer URL
    if (properties._s3Url.startsWith("http")) {
      // If it's already a full S3 URL, check if it needs viewer wrapper
      if (properties._s3Url.includes("/index.html")) {
        return properties._s3Url;
      } else {
        // Convert S3 URL to viewer URL format
        // Extract path from S3 URL (e.g., https://bucket.s3.region.amazonaws.com/path/to/file.pdf)
        const urlObj = new URL(properties._s3Url);
        const path = urlObj.pathname.substring(1); // Remove leading slash
        return `${S3_BASE}/index.html?view=${pathToQueryParam(path)}`;
      }
    } else {
      // Relative path, construct full viewer URL
      return `${S3_BASE}/index.html?view=${pathToQueryParam(
        properties._s3Url
      )}`;
    }
  }

  // Standard WMS feature - use existing logic
  // Choose raw ID: AsBuiltNumber > DrawingNumber > derived from DataSource
  const rawA = properties.AsBuiltNumber || "";
  const rawD = properties.DrawingNumber || "";
  let rawS = "";
  if (!rawA && !rawD) {
    rawS = deriveIdFromDataSource(properties.DataSource || "");
  }
  const raw = rawA || rawD || rawS;
  if (!raw) return "";

  // Normalize; only pad when derived from DataSource
  const fromDS = !rawA && !rawD && rawS;
  let norm = normalizeId(raw);
  if (fromDS) {
    norm = padSuffix3(norm);
  }

  const util = chooseUtilityFolder(layerName, properties);
  const area = getArea(properties);
  const idDir = folderFromId(norm);
  const basePath = `${encodeSegment(ZIP_SEGMENT)}/${encodeSegment(
    area
  )}/${util}/${encodeSegment(idDir)}`;

  if (hasHyphen(norm)) {
    // File link (with hyphen, e.g., K602-001)
    const ext = EXT_ORDER[0];
    const fullPath = `${basePath}/${encodeSegment(norm)}${ext}`;
    return `${S3_BASE}/index.html?view=${pathToQueryParam(fullPath)}`;
  } else {
    // Folder link (no hyphen, e.g., K602)
    const fullPrefix = `${basePath}/`;
    return `${S3_BASE}/index.html?prefix=${pathToQueryParam(fullPrefix)}`;
  }
}

// Determine which field was used for the drawing ID
export function getDrawingIdInfo(properties) {
  // Check if this is a custom AsBuilt file
  if (properties._isCustomAsBuilt) {
    const fileName =
      properties.FileName || properties.AsBuiltNumber || "Custom AsBuilt";
    return { label: "Custom AsBuilt", value: fileName };
  }

  const rawA = properties.AsBuiltNumber || "";
  const rawD = properties.DrawingNumber || "";

  if (rawA) {
    return { label: "AsBuiltNumber", value: normalizeId(rawA) };
  }
  if (rawD) {
    return { label: "DrawingNumber", value: normalizeId(rawD) };
  }

  const rawS = deriveIdFromDataSource(properties.DataSource || "");
  if (rawS) {
    return { label: "DataSource", value: padSuffix3(normalizeId(rawS)) };
  }

  return { label: "", value: "" };
}

// Helper function to build WMS URLs with correct parameter order:
// SERVICE, VERSION, authkey, then other params
export function buildWMSUrl(additionalParams = {}, region = null) {
  const params = new URLSearchParams();

  // Determine workspace from region
  const workspace = getWorkspaceFromRegion(region);
  const baseUrl = buildWMSBaseUrl(workspace);

  // Add SERVICE and VERSION first (use from additionalParams or defaults)
  params.append(
    "SERVICE",
    additionalParams.SERVICE || additionalParams.service || "WMS"
  );
  params.append(
    "VERSION",
    additionalParams.VERSION || additionalParams.version || "1.3.0"
  );

  // Add authkey
  params.append("authkey", WMS_AUTH_KEY);

  // Add all other parameters (excluding SERVICE, VERSION, service, version, authkey)
  Object.entries(additionalParams).forEach(([key, value]) => {
    const upperKey = key.toUpperCase();
    if (
      upperKey !== "SERVICE" &&
      upperKey !== "VERSION" &&
      key !== "authkey" &&
      key !== "service" &&
      key !== "version"
    ) {
      params.append(key, value);
    }
  });

  return `${baseUrl}?${params.toString()}`;
}

// Helper function for WMSTileLayer - returns base URL with authkey
export function buildWMSTileLayerUrl(region = null) {
  return buildWMSUrl(
    {
      SERVICE: "WMS",
      VERSION: "1.3.0",
    },
    region
  );
}

// === BACKEND API CONFIGURATION (for custom AsBuilt files) ===
const CUSTOM_ASBUILT_API = `${process.env.REACT_APP_BACKEND_URL}/custom-asbuilt`;

// === TICKET GEOJSON API CONFIGURATION ===
const GEOJSON_API = `${process.env.REACT_APP_BACKEND_URL}/custom-asbuilt/geojson`;

// Get default visible layer keys for a region
export function getDefaultVisibleLayerKeys(region = null) {
  const layers = getLayersForRegion(region);
  const workspace = getWorkspaceFromRegion(region);

  // For Halton Hills, exclude general infrastructure layers (Civic_Address, Parcel_*)
  if (workspace === "haltonhills") {
    return layers
      .filter(
        (layer) =>
          !layer.key.includes("Civic_Address") && !layer.key.includes("Parcel_")
      )
      .map((layer) => layer.key);
  }

  // For Waterloo and other regions, exclude Roads and Addresses
  return layers
    .filter(
      (layer) =>
        layer.key !== `${workspace}:Roads` &&
        layer.key !== `${workspace}:Addresses`
    )
    .map((layer) => layer.key);
}

// Default visible layers (for backward compatibility - uses Waterloo)
export const DEFAULT_VISIBLE_LAYER_KEYS =
  getDefaultVisibleLayerKeys("waterloo");

// Function to fetch available layers from GetCapabilities
export async function fetchAvailableLayers(region = null) {
  try {
    const capabilitiesUrl = buildWMSUrl(
      {
        SERVICE: "WMS",
        VERSION: "1.3.0",
        request: "GetCapabilities",
      },
      region
    );
    const response = await fetch(capabilitiesUrl);
    const xmlText = await response.text();

    // Parse XML to extract layer names
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");

    // Get all Layer elements that have a Name child
    const layerElements = xmlDoc.querySelectorAll("Layer > Name");
    const layerNames = [];

    layerElements.forEach((nameElement) => {
      const layerName = nameElement.textContent?.trim();
      if (layerName) {
        layerNames.push(layerName);
      }
    });

    return layerNames;
  } catch (error) {
    return [];
  }
}

// Parse URL parameters
export function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const lat = urlParams.get("lat");
  const lng = urlParams.get("lng");
  const radius = urlParams.get("radius");
  const ticketNo = urlParams.get("ticketNo");
  const region = urlParams.get("region");

  return {
    lat: lat ? parseFloat(lat) : undefined,
    lng: lng ? parseFloat(lng) : undefined,
    radius: radius ? parseFloat(radius) : 5, // default 5 meters radius
    ticketNo: ticketNo || undefined,
    region: region || undefined,
  };
}

// Calculate bounding box around a point
export function calculateBoundingBox(lat, lng, radiusMeters) {
  // Approximate degrees per meter (rough calculation)
  const latDegreePerMeter = 1 / 111320;
  const lngDegreePerMeter = 1 / (111320 * Math.cos((lat * Math.PI) / 180));

  const latOffset = radiusMeters * latDegreePerMeter;
  const lngOffset = radiusMeters * lngDegreePerMeter;

  const southwest = L.latLng(lat - latOffset, lng - lngOffset);
  const northeast = L.latLng(lat + latOffset, lng + lngOffset);

  return L.latLngBounds(southwest, northeast);
}

// === MEASUREMENT HELPER FUNCTIONS ===

// Calculate distance between two lat/lng points (Haversine formula)
export function calculateDistance(latlng1, latlng2) {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (latlng1.lat * Math.PI) / 180;
  const φ2 = (latlng2.lat * Math.PI) / 180;
  const Δφ = ((latlng2.lat - latlng1.lat) * Math.PI) / 180;
  const Δλ = ((latlng2.lng - latlng1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

// Calculate total length of a polyline
export function calculatePolylineLength(latlngs) {
  let totalDistance = 0;
  for (let i = 0; i < latlngs.length - 1; i++) {
    totalDistance += calculateDistance(latlngs[i], latlngs[i + 1]);
  }
  return totalDistance;
}

// Calculate area of a polygon using Shoelace formula
export function calculatePolygonArea(latlngs) {
  const R = 6371e3; // Earth's radius in meters
  let area = 0;

  if (latlngs.length < 3) return 0;

  for (let i = 0; i < latlngs.length; i++) {
    const j = (i + 1) % latlngs.length;
    const xi = latlngs[i].lng * (Math.PI / 180);
    const yi = latlngs[i].lat * (Math.PI / 180);
    const xj = latlngs[j].lng * (Math.PI / 180);
    const yj = latlngs[j].lat * (Math.PI / 180);

    area += xi * Math.sin(yj) - xj * Math.sin(yi);
  }

  area = Math.abs(area * R * R) / 2;
  return area;
}

// Calculate bearing between two points
export function calculateBearing(latlng1, latlng2) {
  const φ1 = (latlng1.lat * Math.PI) / 180;
  const φ2 = (latlng2.lat * Math.PI) / 180;
  const Δλ = ((latlng2.lng - latlng1.lng) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);
  const bearing = ((θ * 180) / Math.PI + 360) % 360;

  return bearing;
}

// Unit conversion functions
export function metersToFeet(meters) {
  return meters * 3.28084;
}

export function metersToMiles(meters) {
  return meters * 0.000621371;
}

export function sqMetersToSqFeet(sqMeters) {
  return sqMeters * 10.7639;
}

export function sqMetersToAcres(sqMeters) {
  return sqMeters * 0.000247105;
}

export function sqMetersToHectares(sqMeters) {
  return sqMeters * 0.0001;
}

// Format distance for display based on selected unit
export function formatDistance(meters, unit) {
  if (unit === "imperial") {
    const feet = metersToFeet(meters);
    if (feet < 5280) {
      return `${feet.toFixed(3)} ft`;
    }
    return `${metersToMiles(meters).toFixed(4)} mi`;
  } else if (unit === "mixed") {
    const feet = metersToFeet(meters);
    return `${meters.toFixed(3)} m (${feet.toFixed(3)} ft)`;
  } else {
    // metric
    if (meters < 1000) {
      return `${meters.toFixed(3)} m`;
    }
    return `${(meters / 1000).toFixed(4)} km`;
  }
}

// Format area for display based on selected unit
export function formatArea(squareMeters, unit) {
  if (unit === "imperial") {
    const sqFeet = sqMetersToSqFeet(squareMeters);
    if (sqFeet < 43560) {
      return `${sqFeet.toFixed(3)} ft²`;
    }
    return `${sqMetersToAcres(squareMeters).toFixed(4)} acres`;
  } else if (unit === "mixed") {
    const sqFeet = sqMetersToSqFeet(squareMeters);
    return `${squareMeters.toFixed(3)} m² (${sqFeet.toFixed(3)} ft²)`;
  } else {
    // metric
    if (squareMeters < 10000) {
      return `${squareMeters.toFixed(3)} m²`;
    }
    const hectares = sqMetersToHectares(squareMeters);
    if (hectares < 100) {
      return `${hectares.toFixed(4)} ha`;
    }
    return `${(squareMeters / 1000000).toFixed(4)} km²`;
  }
}

// Format bearing for display
export function formatBearing(degrees) {
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(degrees / 45) % 8;
  return `${degrees.toFixed(2)}° (${directions[index]})`;
}

// Function to search nearby infrastructure features
export async function searchNearbyFeatures(
  coordinates,
  radiusMeters = 100,
  layerKeys,
  region = null
) {
  try {
    const [lat, lng] = coordinates;
    const bbox = calculateBoundingBox(lat, lng, radiusMeters);

    const results = await Promise.all(
      layerKeys.map(async (layerKey) => {
        try {
          const wfsUrl = buildWMSUrl(
            {
              SERVICE: "WFS",
              VERSION: "2.0.0",
              request: "GetFeature",
              typeName: layerKey,
              outputFormat: "application/json",
              maxFeatures: "50",
              bbox: `${bbox.getWest()},${bbox.getSouth()},${bbox.getEast()},${bbox.getNorth()},EPSG:4326`,
            },
            region
          );

          const response = await fetch(wfsUrl);
          const data = await response.json();

          return {
            layerKey,
            features: data.features || [],
          };
        } catch (error) {
          return { layerKey, features: [] };
        }
      })
    );

    return results.filter((result) => result.features.length > 0);
  } catch (error) {
    return [];
  }
}

// === CUSTOM ASBUILT FILES FUNCTIONS ===

// Fetch all custom AsBuilt files from backend API
export async function fetchAllCustomAsBuiltFiles() {
  try {
    const response = await fetch(CUSTOM_ASBUILT_API);
    if (!response.ok) {
      return [];
    }

    const responseData = await response.json();

    // Handle nested response structure: { success: true, data: [...] } or direct array
    let files = [];
    if (Array.isArray(responseData)) {
      files = responseData;
    } else if (responseData?.data && Array.isArray(responseData.data)) {
      files = responseData.data;
    }

    return files;
  } catch (error) {
    return [];
  }
}

// Check if a point is within radius of a custom AsBuilt file
export function isPointWithinRadius(
  pointLat,
  pointLng,
  asBuiltLat,
  asBuiltLng,
  radiusMeters
) {
  const distance = calculateDistance(
    L.latLng(pointLat, pointLng),
    L.latLng(asBuiltLat, asBuiltLng)
  );
  return distance <= radiusMeters;
}

// Convert custom AsBuilt file to GeoJSON feature format
export function customAsBuiltToFeature(asBuilt) {
  return {
    id: `custom-asbuilt-${asBuilt.id || Date.now()}`,
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [asBuilt.lng, asBuilt.lat],
    },
    properties: {
      // Mark as custom AsBuilt
      _isCustomAsBuilt: true,
      // Store the S3 URL directly (relative path from backend)
      _s3Url: asBuilt.s3Url,
      // Standard properties for display
      AsBuiltNumber: asBuilt.fileName || asBuilt.id || "Custom AsBuilt",
      DrawingNumber: asBuilt.fileName || asBuilt.id || "Custom AsBuilt",
      FileName: asBuilt.fileName || "Custom AsBuilt",
      UploadedAt: asBuilt.uploadedAt || new Date().toISOString(),
      Radius: asBuilt.radius || 0,
      FileSize: asBuilt.fileSize || 0,
      MimeType: asBuilt.mimeType || "application/octet-stream",
      TicketNumber: asBuilt.ticketNumber || "",
      // Include metadata object if present
      ...(asBuilt.metadata || {}),
      // Preserve original backend fields for reference
      _originalId: asBuilt.id,
      _originalData: {
        id: asBuilt.id,
        fileName: asBuilt.fileName,
        s3Url: asBuilt.s3Url,
        lat: asBuilt.lat,
        lng: asBuilt.lng,
        radius: asBuilt.radius,
        uploadedAt: asBuilt.uploadedAt,
        fileSize: asBuilt.fileSize,
        mimeType: asBuilt.mimeType,
        ticketNumber: asBuilt.ticketNumber,
        metadata: asBuilt.metadata,
      },
    },
  };
}

// Get custom AsBuilt files that contain the clicked point
export function getCustomAsBuiltFilesAtPoint(lat, lng, customAsBuiltFiles) {
  if (!customAsBuiltFiles || customAsBuiltFiles.length === 0) {
    return [];
  }

  // Filter files where the clicked point is within the file's radius
  const matchingFiles = customAsBuiltFiles.filter((file) => {
    const radius = file.radius || 100;
    const distance = calculateDistance(
      L.latLng(lat, lng),
      L.latLng(file.lat, file.lng)
    );
    return distance <= radius;
  });

  return matchingFiles.map(customAsBuiltToFeature);
}

// Fetch ticket GeoJSON data from API
export async function fetchTicketGeoJSON(ticketNo) {
  try {
    if (!ticketNo) return null;

    const response = await fetch(`${GEOJSON_API}?ticketNo=${ticketNo}`);
    if (!response.ok) {
      return null;
    }

    const responseData = await response.json();

    // Handle response structure: { success: true, data: [...] }
    if (
      responseData?.success &&
      responseData?.data &&
      Array.isArray(responseData.data)
    ) {
      // Extract GeoJSON from the first item (or merge all if multiple)
      // The API returns array of items, each with geojsonData field
      const allFeatures = [];

      responseData.data.forEach((item) => {
        if (item.geojsonData && item.geojsonData.features) {
          allFeatures.push(...item.geojsonData.features);
        }
      });

      if (allFeatures.length > 0) {
        // Return merged FeatureCollection
        return {
          type: "FeatureCollection",
          features: allFeatures,
        };
      }
    }

    return null;
  } catch (error) {
    console.error("Error fetching ticket GeoJSON:", error);
    return null;
  }
}

// Build a WMS 1.3.0 GetFeatureInfo URL for all currently visible layers
export function buildGetFeatureInfoUrl(
  map,
  latlng,
  visibleLayerKeys,
  region = null
) {
  if (!visibleLayerKeys.length) return null;

  const point = map.latLngToContainerPoint(latlng);
  const size = map.getSize();

  // Always use current map bounds for GetFeatureInfo bbox
  const bounds = map.getBounds();

  // Use EPSG:3857 to avoid axis-order confusion; project bounds to meters.
  const sw = L.CRS.EPSG3857.project(bounds.getSouthWest());
  const ne = L.CRS.EPSG3857.project(bounds.getNorthEast());
  const bbox = [sw.x, sw.y, ne.x, ne.y].join(",");

  return buildWMSUrl(
    {
      SERVICE: "WMS",
      VERSION: "1.3.0",
      request: "GetFeatureInfo",
      layers: visibleLayerKeys.join(","), // which layers are drawn
      query_layers: visibleLayerKeys.join(","), // which layers to query
      info_format: "application/json", // try JSON first
      crs: "EPSG:3857",
      bbox,
      width: String(size.x),
      height: String(size.y),
      i: String(Math.round(point.x)), // WMS 1.3.0 uses i/j
      j: String(Math.round(point.y)),
      feature_count: "50",
    },
    region
  );
}

// === WMS-BROKEN LAYER FALLBACK ===
// Some GeoServer layers fail to render through the WMS GetMap/GetFeatureInfo
// pipeline (confirmed: blank output regardless of style/bbox, and empty
// GetFeatureInfo results at a pixel directly over a known feature) even though
// the same layer returns valid data over WFS. For those, we fetch the data via
// WFS and render it client-side instead of using WMSTileLayer.
// Verified broken on GeoServer (waterloo workspace) as of 2026-09-16: Water_Chambers.
const WMS_BROKEN_LAYER_NAMES = new Set(["Water_Chambers"]);

export function isWMSRenderingBroken(layerKey) {
  const layerName = layerKey.split(":").pop();
  return WMS_BROKEN_LAYER_NAMES.has(layerName);
}

// Fetch all features for a layer via WFS (used as the rendering path for
// layers listed in WMS_BROKEN_LAYER_NAMES)
export async function fetchLayerFeaturesViaWFS(layerKey, region = null) {
  try {
    const url = buildWMSUrl(
      {
        SERVICE: "WFS",
        VERSION: "2.0.0",
        request: "GetFeature",
        typeName: layerKey,
        outputFormat: "application/json",
        maxFeatures: "2000",
      },
      region
    );
    const response = await fetch(url);
    if (!response.ok) return [];
    const data = await response.json();
    return data.features || [];
  } catch (error) {
    return [];
  }
}
